"use client";
import type { Locale, TargetLanguage } from "@mw/shared/i18n";

import { usePathname, useRouter } from "@/i18n/navigation";
import { LocalStorageKeys, safeLocalStorage } from "@utilities/localStorage";
import { defaultLocale, releasedLocales } from "@mw/shared/i18n";
import { ChevronRight, XIcon } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { useLocale } from "next-intl";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Button } from "../core/Button";
import DynamicFlag from "../languages/DynamicFlag";
import { LOCALIZED_LABELS } from "./languageUtils";

interface LanguageData {
  lastPrompt: number;
  preferred: Locale;
}

function getBrowserLocale(): Locale | null {
  const lang = navigator.language;
  const shortLang = lang.split("-")[0];
  return releasedLocales.includes(shortLang as Locale) ? (shortLang as Locale) : null;
}

function loadLanguageData() {
  try {
    return JSON.parse(safeLocalStorage.getItem(LocalStorageKeys.LANGUAGE_PREFERENCE) || "{}") || {};
  } catch {
    return {};
  }
}

function saveLanguageData(data: LanguageData) {
  safeLocalStorage.setItem(LocalStorageKeys.LANGUAGE_PREFERENCE, JSON.stringify(data));
}

export function shouldSuggestLocaleSwitch(currentLocale: Locale) {
  const browserLocale = getBrowserLocale();
  const savedLocale = loadLanguageData().preferred as Locale | undefined;

  // If browser locale is the same as current locale, no need to suggest
  if (browserLocale && browserLocale === currentLocale) {
    return false;
  }

  // If saved locale is the same as current locale, no need to suggest
  if (savedLocale && savedLocale === currentLocale) {
    return false;
  }

  // If browser locale is supported, suggest it
  if (browserLocale && releasedLocales.includes(browserLocale)) {
    return {
      suggest: browserLocale,
    };
  }

  // If browser locale is not supported and current locale is not default, suggest default
  if (!browserLocale && currentLocale !== (defaultLocale as TargetLanguage)) {
    return {
      suggest: defaultLocale as TargetLanguage,
    };
  }

  return false;
}

export function recordLanguageDecision(locale: Locale) {
  saveLanguageData({
    preferred: locale,
    lastPrompt: Date.now(),
  });
}

// Static translations for banner text (outside i18n since we need to show in browser locale)
const BANNER_MESSAGES: Record<Locale, { message: string; switchButton: string; title: string }> = {
  en: {
    title: "Select preferred language",
    message:
      "We noticed you are browsing in {currentLang}. Would you prefer to view the site in {suggestedLang} instead?",
    switchButton: "Switch to {language}",
  },
  es: {
    title: "Seleccionar idioma preferido",
    message: "Notamos que estás navegando en {currentLang}. ¿Preferirías ver el sitio en {suggestedLang}?",
    switchButton: "Cambiar a {language}",
  },
  fr: {
    title: "Sélectionner la langue préférée",
    message:
      "Nous avons remarqué que vous naviguez en {currentLang}. Préféreriez-vous voir le site en {suggestedLang} ?",
    switchButton: "Passer à {language}",
  },
  de: {
    title: "Bevorzugte Sprache auswählen",
    message:
      "Wir haben bemerkt, dass Sie in {currentLang} browsen. Möchten Sie die Site lieber in {suggestedLang} anzeigen?",
    switchButton: "Zu {language} wechseln",
  },
  hi: {
    title: "पसंदीदा भाषा चुनें",
    message:
      "हमने देखा है कि आप {currentLang} में ब्राउज़ कर रहे हैं। क्या आप साइट को {suggestedLang} में देखना पसंद करेंगे?",
    switchButton: "{language} पर स्विच करें",
  },
  ja: {
    title: "優先言語を選択",
    message:
      "{currentLang}でブラウジングしていることに気づきました。代わりに{suggestedLang}でサイトをご覧になりますか？",
    switchButton: "{language}に切り替える",
  },
  pt: {
    title: "Selecionar idioma preferido",
    message: "Notamos que você está navegando em {currentLang}. Você gostaria de ver o site em {suggestedLang}?",
    switchButton: "Mudar para {language}",
  },
  sv: {
    title: "Välj önskat språk",
    message:
      "Vi märkte att du surfar på {currentLang}. Skulle du föredra att se webbplatsen på {suggestedLang} istället?",
    switchButton: "Byt till {language}",
  },
  pl: {
    title: "Wybierz preferowany język",
    message:
      "Zauważyliśmy, że przeglądasz w języku {currentLang}. Czy wolałbyś zobaczyć stronę w języku {suggestedLang}?",
    switchButton: "Przełącz na {language}",
  },
  ko: {
    title: "선호 언어 선택",
    message: "{currentLang}(으)로 탐색 중이신 것을 확인했습니다. {suggestedLang}(으)로 사이트를 보시겠습니까?",
    switchButton: "{language}(으)로 전환",
  },
  it: {
    title: "Seleziona la lingua preferita",
    message:
      "Abbiamo notato che stai navigando in {currentLang}. Preferiresti visualizzare il sito in {suggestedLang}?",
    switchButton: "Passa a {language}",
  },
  zh: {
    title: "选择语言",
    message: "检测到你正在使用 {currentLang} 浏览。是否切换到 {suggestedLang}？",
    switchButton: "切换到 {language}",
  },
};

function getBannerText(toLocale: Locale, currentLocale: Locale) {
  const messages = BANNER_MESSAGES[toLocale] || BANNER_MESSAGES.en;

  return {
    title: messages.title,
    message: messages.message
      .replace("{currentLang}", `<strong>${LOCALIZED_LABELS[currentLocale]}</strong>`)
      .replace("{suggestedLang}", `<strong>${LOCALIZED_LABELS[toLocale]}</strong>`),
    switchButton: messages.switchButton.replace("{language}", LOCALIZED_LABELS[toLocale]),
  };
}

export default function LanguageChangeBanner() {
  const locale = useLocale() as Locale;
  const [suggestion, setSuggestion] = useState<{ suggest: Locale } | null>(null);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [closeBanner, setCloseBanner] = useState(false);
  const router = useRouter();

  useEffect(() => {
    const result = shouldSuggestLocaleSwitch(locale);
    if (result) {
      setSuggestion(result);
    }
  }, [locale]);

  // Get text in target locale (the language we're suggesting)
  const bannerText = useMemo(() => {
    if (!suggestion) return null;

    return getBannerText(suggestion.suggest, locale);
  }, [suggestion, locale]);

  const switchUrl = useMemo(() => {
    if (!suggestion) return "";
    const params = new URLSearchParams(searchParams.toString());
    const urlParams = params.size > 0 ? `?${params.toString()}` : "";
    const nextUrl =
      suggestion.suggest === "en"
        ? `${pathname}${urlParams}`
        : `/${suggestion.suggest}${pathname === "/" ? "" : pathname}${urlParams}`;
    return nextUrl;
  }, [searchParams, pathname, suggestion]);

  const handleCloseBanner = useCallback(() => {
    setCloseBanner(true);
    recordLanguageDecision(locale);
  }, [locale]);

  const handleSwitchLanguage = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!suggestion) return;
      if (suggestion.suggest === "en") {
        e.preventDefault();
        recordLanguageDecision(suggestion.suggest);
        router.replace(switchUrl, { locale: "en" });
        return;
      }
      recordLanguageDecision(suggestion.suggest);
    },
    [suggestion, switchUrl, router],
  );

  if (!suggestion || closeBanner || !bannerText) return null;

  return (
    <div className='w-9/10 fixed bottom-16 z-9999 mx-8 rounded-xl border border-default bg-overlay-lightest shadow-lg backdrop-blur-lg md:right-16 md:mx-0 md:w-[500px]'>
      <div className='p-20'>
        <p className='f-subhead-03 mb-24 normal-case'>{bannerText.title}</p>
        <p className='f-ui-02 mb-2' dangerouslySetInnerHTML={{ __html: bannerText.message }} />
      </div>
      <Button
        className='absolute right-6 top-6 border border-default'
        onClick={handleCloseBanner}
        size='pill'
        variant='elevated'
      >
        <XIcon size={16} />
      </Button>
      <div className='h-1 w-full bg-dark' />
      <div className='flex items-center justify-between p-20'>
        <div className='flex items-center gap-4'>
          <DynamicFlag className='h-24 w-24 rounded-full' locale={locale} />
          <ChevronRight size={16} />
          <DynamicFlag className='h-24 w-24 rounded-full' locale={suggestion.suggest} />
        </div>
        <Button asChild onClick={handleSwitchLanguage} variant='primary'>
          <a href={switchUrl}>{bannerText.switchButton}</a>
        </Button>
      </div>
    </div>
  );
}
